import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Stack
} from '@mui/material';
import { Search, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTranslation } from 'react-i18next';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { getUser } from '../utils/userUtils';

interface RunnerSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  personId?: number;
}

const getRunnerDisplayLabel = (runner: RunnerSearchResult): string => {
  const firstName = (runner.firstName || '').trim();
  const lastName = (runner.lastName || '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  return (runner.displayName || '').trim();
};

const resolveRunnerId = (runner: RunnerSearchResult): string => {
  if (typeof runner.personId === 'number' && Number.isFinite(runner.personId)) {
    return String(runner.personId);
  }
  return runner.id;
};

const RunnerSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Listen for auth state
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setCurrentUser(u));
    return unsub;
  }, []);
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<RunnerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [myPersonId, setMyPersonId] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Load current user's personId
  useEffect(() => {
    const loadMyPersonId = async () => {
      if (currentUser?.uid) {
        try {
          const profile = await getUser(currentUser.uid);
          if (profile?.personId) {
            setMyPersonId(profile.personId);
          }
        } catch (err) {
          console.warn('Failed to load user profile for personId:', err);
        }
      } else {
        setMyPersonId(null);
      }
    };
    loadMyPersonId();
  }, [currentUser]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText.trim().length >= 2) {
        performSearch(searchText.trim());
      } else if (searchText.trim().length === 0) {
        setResults([]);
        setHasSearched(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchText]);

  const performSearch = async (search: string) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const profilesRef = collection(db, 'publicProfiles');
      const searchLower = search.toLowerCase();
      const snapshot = await getDocs(profilesRef);

      // Client-side filtering for name matching
      const allUsers = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as RunnerSearchResult;
        const { id: _ignoredId, ...rest } = data || {};
        return {
          id: docSnap.id,
          ...rest
        };
      });
      
      const filtered = allUsers.filter(user => {
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = (user.displayName || '').toLowerCase();
        return (
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          (fullName && fullName.includes(searchLower)) ||
          displayName.includes(searchLower)
        );
      });
      
      // Sort by last name, then first name
      filtered.sort((a, b) => {
        const aLast = (a.lastName || '').trim();
        const bLast = (b.lastName || '').trim();
        const aFirst = (a.firstName || '').trim();
        const bFirst = (b.firstName || '').trim();
        if (aLast || bLast) {
          const lastNameCompare = aLast.localeCompare(bLast);
          if (lastNameCompare !== 0) return lastNameCompare;
          return aFirst.localeCompare(bFirst);
        }
        return (a.displayName || '').localeCompare(b.displayName || '');
      });
      
      setResults(filtered);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRunnerClick = (runner: RunnerSearchResult) => {
    const targetId = resolveRunnerId(runner);
    navigate(`/runners/${targetId}`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          {t('runners.search.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('runners.search.subtitle')}
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          id="runner-search"
          name="runnerSearch"
          label={t('runners.search.searchLabel')}
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('runners.search.searchPlaceholder')}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={24} />
              </InputAdornment>
            ) : null
          }}
          helperText={t('runners.search.searchHelperText')}
        />
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {hasSearched && !loading && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {t('runners.search.resultsTitle')}
            </Typography>
            <Chip 
              label={t('runners.search.resultsCount', { count: results.length })} 
              color={results.length > 0 ? 'primary' : 'default'}
              size="small"
            />
          </Box>

          {results.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <UserIcon size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="body1" color="text.secondary">
                {t('runners.search.noResults', { query: searchText })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('runners.search.noResultsHint')}
              </Typography>
            </Box>
          ) : (
            <List>
              {results.map((runner, index) => (
                <ListItem 
                  key={runner.id} 
                  disablePadding
                  divider={index < results.length - 1}
                >
                  <ListItemButton onClick={() => handleRunnerClick(runner)}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <UserIcon size={20} />
                          <Typography variant="body1">
                            {getRunnerDisplayLabel(runner)}
                          </Typography>
                          {myPersonId && runner.personId === myPersonId && (
                            <Chip 
                              label={t('runners.search.myProfile')} 
                              color="success" 
                              size="small" 
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={runner.personId ? t('runners.search.runnerId', { id: runner.personId }) : undefined}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Initial State */}
      {!hasSearched && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Search size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('runners.search.initialTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('runners.search.initialHint')}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default RunnerSearchPage;
